import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import {
  useUpdateOnboardingDocument,
  type OnboardingDoc,
} from '../../hooks/useOnboardingDocuments';

/**
 * Document-level signing settings: whether the inductee must pick an option,
 * the option list, and whether the "I have read and understood" confirmation
 * shows. These live on the document (not the PDF version) so they persist
 * across uploads and save inline, like the HR sign-off / pre-employment controls.
 *
 * The two toggles save instantly on change; the option text saves on an
 * explicit "Save options" click so a request doesn't fire per keystroke.
 */
export function DocSignSettings({ doc }: { doc: OnboardingDoc }) {
  const update = useUpdateOnboardingDocument();
  const [options, setOptions] = useState<string[]>(
    doc.options.length > 0 ? doc.options.map((o) => o.label) : ['', ''],
  );
  const [ackText, setAckText] = useState(doc.ack_text ?? '');

  const DEFAULT_ACK = 'I have read and understood this document.';

  function saveAck() {
    update.mutate(
      { id: doc.id, ack_text: ackText.trim() || null },
      { onSuccess: () => toast.success('Wording saved') },
    );
  }

  function setOptionAt(i: number, value: string) {
    setOptions((prev) => prev.map((o, j) => (j === i ? value : o)));
  }

  function saveOptions() {
    const built = options
      .map((o) => o.trim())
      .filter(Boolean)
      .map((o) => ({ value: o, label: o }));
    if (built.length < 2) {
      toast.error('Add at least two options.');
      return;
    }
    update.mutate(
      { id: doc.id, options: built },
      { onSuccess: () => toast.success('Options saved') },
    );
  }

  return (
    <div className="space-y-3 border-t border-line/10 pt-3 text-sm">
      <label className="flex items-center gap-2 text-fg">
        <input
          type="checkbox"
          checked={doc.requires_option}
          disabled={update.isPending}
          onChange={(e) => update.mutate({ id: doc.id, requires_option: e.target.checked })}
        />
        Make the inductee pick an option before signing
      </label>

      {doc.requires_option ? (
        <div className="space-y-2 rounded-token-sm bg-surface-2 p-3">
          <span className="block text-xs font-medium text-fg-muted">
            Options the inductee picks from
          </span>
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                value={opt}
                onChange={(e) => setOptionAt(i, e.target.value)}
                placeholder={
                  i === 0
                    ? 'e.g. I will use the company vehicle for private use'
                    : i === 1
                      ? 'e.g. I will not use the company vehicle for private use'
                      : 'Another option'
                }
                className="flex-1 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm text-fg"
              />
              <button
                type="button"
                onClick={() => setOptions((prev) => (prev.length > 1 ? prev.filter((_, j) => j !== i) : prev))}
                disabled={options.length <= 1}
                aria-label="Remove option"
                className="text-fg-dim hover:text-danger disabled:opacity-40"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setOptions((prev) => [...prev, ''])}
              className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
            >
              <Plus className="h-3.5 w-3.5" /> Add option
            </button>
            <Button size="sm" variant="primary" onClick={saveOptions} loading={update.isPending}>
              Save options
            </Button>
          </div>
          <p className="text-xs text-fg-dim">
            The inductee sees these as a required list above the signature and their choice is
            printed on the signed PDF. Use Preview to see it.
          </p>
        </div>
      ) : null}

      <label className="flex items-center gap-2 text-fg">
        <input
          type="checkbox"
          checked={doc.require_read}
          disabled={update.isPending}
          onChange={(e) => update.mutate({ id: doc.id, require_read: e.target.checked })}
        />
        Show the "I have read and understood" confirmation
        <span className="text-fg-dim">— turn off to let the choice be the acknowledgement</span>
      </label>

      {doc.require_read ? (
        <div className="space-y-1.5 rounded-token-sm bg-surface-2 p-3">
          <span className="block text-xs font-medium text-fg-muted">
            Acknowledgement wording the inductee ticks
          </span>
          <textarea
            rows={4}
            value={ackText}
            onChange={(e) => setAckText(e.target.value)}
            placeholder={DEFAULT_ACK}
            className="block w-full rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm text-fg"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-fg-dim">Leave blank to use the default: "{DEFAULT_ACK}"</span>
            <Button size="sm" variant="primary" onClick={saveAck} loading={update.isPending}>
              Save wording
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
